import React from 'react';
import styles from './styles.module.scss';

export type ButtonVariant = 'solid' | 'outlined' | 'ghost';
export type ButtonSize = 'sm' | 'md';
export type ButtonColor = 'default' | 'accent' | 'accept' | 'decline';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Button variant */
  variant?: ButtonVariant;
  /** Button size */
  size?: ButtonSize;
  /** Button color scheme */
  color?: ButtonColor;
  /** Icon element to display before text */
  startIcon?: React.ReactNode;
  /** Icon element to display after text */
  endIcon?: React.ReactNode;
  /** Full width button */
  fullWidth?: boolean;
  /** Additional className */
  className?: string;
  /** Button content */
  children: React.ReactNode;
}

/**
 * Reusable Button component.
 * Supports multiple variants (solid, outlined, ghost), sizes, and color schemes.
 */
export const Button: React.FC<ButtonProps> = ({
  variant = 'solid',
  size = 'md',
  color = 'default',
  startIcon,
  endIcon,
  fullWidth = false,
  className,
  children,
  disabled,
  ...props
}) => {
  const classNames = [
    styles.btn,
    styles[`btn--${variant}`],
    styles[`btn--${size}`],
    color !== 'default' && styles[`btn--${color}`],
    fullWidth && styles['btn--fullWidth'],
    disabled && styles['btn--disabled'],
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button className={classNames} disabled={disabled} {...props}>
      {startIcon && <span className={styles.icon}>{startIcon}</span>}
      {children}
      {endIcon && <span className={styles.icon}>{endIcon}</span>}
    </button>
  );
};

export default Button;
